import { useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <img src="/pruva-logo-full.png" alt="Pruva Swimming Pools" className="h-12 w-auto" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Project Tracking</span>
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{t("forgot.title")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("forgot.subtitle")}</p>

        {done ? (
          <div className="mt-6 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid="forgot-success">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            {t("forgot.success")}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="forgot-error">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" placeholder="you@company.com" />
            </div>
            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="forgot-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("forgot.sendReset")}
            </Button>
          </form>
        )}
        <div className="mt-4 text-center">
          <Link to="/login" className="text-sm text-sky-700 hover:underline" data-testid="back-to-login-link">
            {t("forgot.backToSignIn")}
          </Link>
        </div>
      </div>
    </div>
  );
}
